import React, { useEffect, useState } from 'react';
import './i18n';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';
import { useAppCtx } from './app-ctx';
import iconClosed from './img/indicator-icon-closed.svg';
import iconOpened from './img/indicator-icon-opened.svg';

/**
 * My Booking — accordion-like master-detail list of the current user's bookings.
 *
 * Each row shows: booking id, check-in date, resort name, resort address and an
 * expand/collapse toggle. Expanding a row reveals the booking details.
 */
const MyBooking = () => {
  const { t } = useTranslation();
  const { JOOMLA_VARS, CONFIG } = useAppCtx();
  const appRoot = JOOMLA_VARS.root;

  const [bookings, setBookings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // booking pending cancellation
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    // Abort on unmount so the response never sets state on a dead component
    const controller = new AbortController();
    fetch(appRoot + CONFIG.getBookings, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': JOOMLA_VARS.csrfToken || '' },
      body: JSON.stringify({}),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) 
          return;
        setBookings(data.bookings || []);
        setLoaded(true);
      })
      .catch((e) => {
        // Ignore cancellations caused by cleanup (unmount)
        if (e && e.name === 'AbortError') 
          return;
        console.warn('MyBooking: failed to load bookings', e);
      });

    return () => {
      controller.abort();
    };
  }, []);

  if (!loaded) {
    return <div className="loading">{t('Loading ...')}</div>;
  }

  if (bookings.length === 0) {
    return (
      <div className="my-booking">
        <h2>{t('My Booking')}</h2>
        <div className="my-booking-empty">{t('No bookings yet')}</div>
      </div>
    );
  }

  // Format date according to the locale/language currently selected by the user
  const formatDate = (iso) => {
    if (!iso) 
      return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) 
      return iso;
    // English uses the United Kingdom locale (en-GB) for date formatting.
    const locale = (i18n.language || '').startsWith('en') ? 'en-GB' : i18n.language;
    const result = d.toLocaleDateString(locale || undefined);
    return result;
  };

  const toggle = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  // Ask the backend to cancel the given booking.
  const doCancelBooking = (booking) => {
    setCancelling(true);
    fetch(appRoot + CONFIG.cancelBooking, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': JOOMLA_VARS.csrfToken || '' },
      body: JSON.stringify({ bookingId: booking.id }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error && data.status === 'ok') {
          setBookings((current) =>
            current.map((b) => (b.id === booking.id ? { ...b, orderStatus: 'canceling' } : b))
          );
        } else {
          console.warn('MyBooking: cancel failed', data && data.error);
        }
      })
      .catch((e) => console.warn('MyBooking: cancel request failed', e))
      .finally(() => {
        setCancelling(false);
        setCancelTarget(null);
      });
  };

  return (
    <div className="my-booking">
      <h2>{t('My Booking')}</h2>
      <div className="booking-list">
        {bookings.map((b) => {
          const isOpen = expandedId === b.id;
          const guests = Array.isArray(b.guests) ? b.guests : [];
          const adults = guests.filter((g) => (g.age || 0) >= 18).length;
          const children = guests.length - adults;
          const isCanceled = b.orderStatus === 'canceled';
          const isCanceling = b.orderStatus === 'canceling';

          return (
            <div key={b.id} className={`booking-item${isOpen ? ' is-open' : ''}`}>
              <div
                className="booking-item-header"
                role="button"
                tabIndex={0}
                onClick={() => toggle(b.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(b.id);
                  }
                }}
              >
                <span className="booking-id">#{b.id}</span>
                <span className="booking-checkin">{formatDate(b.checkInDate)}</span>
                <span className="booking-resort">{b.resortName}</span>
                <span className="booking-address">{b.resortAddress}</span>
                {isCanceled && <span className="booking-status canceled">{t('Canceled')}</span>}
                {isCanceling && <span className="booking-status canceling">{t('Cancel request sent')}</span>}
                <span className="booking-toggle" aria-hidden="true">
                  <img
                    src={isOpen ? iconOpened : iconClosed}
                    alt=""
                    width="16"
                    height="16"
                  />
                </span>
              </div>

              {isOpen && (
                <div className="booking-item-details">
                  <p>
                    <strong>{t('Room')}:</strong> {b.roomName}
                  </p>
                  <p>
                    <strong>{t('Check-in')}:</strong> {formatDate(b.checkInDate)}
                  </p>
                  <p>
                    <strong>{t('Check-out')}:</strong> {formatDate(b.checkOutDate)}
                  </p>
                  <p>
                    <strong>{t('Guests')}:</strong> {adults} {t('Adult(s)')}, {children}{' '}
                    {t('Child(ren)')}
                  </p>
                  {b.totalPrice && (
                    <p>
                      <strong>{t('Total')}:</strong> {b.totalPrice} {b.currencySymbol}
                    </p>
                  )}
                  {b.message && (
                    <p>
                      <strong>{t('Message')}:</strong> {b.message}
                    </p>
                  )}
                  {!isCanceled && !isCanceling && (
                    <button
                      type="button"
                      className="btn-cancel-booking"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCancelTarget(b);
                      }}
                    >
                      {t('Cancel Booking')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Cancel booking confirmation popup (no window.confirm) */}
      {cancelTarget && (
        <div
          className="cancel-booking-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => !cancelling && setCancelTarget(null)}
        >
          <div className="cancel-booking-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{t('Cancel Booking')}</h3>
            <p>
              {t('Are you sure you want to cancel booking')} #{cancelTarget.id}?
            </p>
            <div className="cancel-booking-actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={cancelling}
                onClick={() => setCancelTarget(null)}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={cancelling}
                onClick={() => doCancelBooking(cancelTarget)}
              >
                {cancelling ? t('Canceling ...') : t('Yes, cancel booking')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBooking;